import { Link } from '@tanstack/react-router'
import type { MessagingConversation } from '../../../api/generated/model/messagingConversation'
import { useT } from '../../../i18n'
import { messagingI18n } from '../../../i18n/messaging'
import type { MessagingSurface } from './MessageBubble'
import { validateEntityRoute } from './entity-safety'

type EntityConversationPanelProps = {
  conversation: MessagingConversation
  surface: MessagingSurface
  variant?: 'default' | 'compact' | 'expanded'
}

const linkClass =
  'mt-1 inline-flex text-sm font-semibold text-[var(--color-action)] hover:underline focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)]'

export function EntityConversationPanel({
  conversation,
  surface,
  variant = 'default',
}: EntityConversationPanelProps) {
  const t = useT(messagingI18n)
  const route = validateEntityRoute(conversation, surface)
  const linkLabel = {
    order: t.conversation.linkedOrder,
    work_order: t.conversation.linkedWorkOrder,
    purchase_order: t.conversation.linkedPurchaseOrder,
    sample: t.conversation.linkedSample,
    organization: t.conversation.linkedSupport,
  }[conversation.entity_link.entity_type]
  const isSupportConversation =
    conversation.entity_link.entity_type === 'organization'

  if (variant === 'compact') {
    return (
      <aside
        className="flex min-w-0 items-center gap-2 border-y border-white/10 bg-white/[0.04] px-4 py-2 text-xs text-slate-300"
        data-variant="compact"
      >
        <span className="shrink-0 font-semibold text-slate-400">
          {linkLabel}
        </span>
        <span aria-hidden="true" className="text-slate-600">
          ·
        </span>
        <span className="truncate font-medium text-slate-200">
          {conversation.entity_link.label}
        </span>
        {!route && surface !== 'admin' && !isSupportConversation ? (
          <span className="sr-only" role="status">
            {t.states.projectionUnavailable}
          </span>
        ) : null}
      </aside>
    )
  }

  const entityLink = (() => {
    switch (route?.kind) {
      case 'seller_order':
        return (
          <Link to="/seller/my-orders/$id" params={{ id: route.id }} className={linkClass}>
            {conversation.entity_link.label}
          </Link>
        )
      case 'supply_work_order':
        return (
          <Link to="/supply/work-orders/$id" params={{ id: route.id }} className={linkClass}>
            {conversation.entity_link.label}
          </Link>
        )
      case 'supply_sample':
        return (
          <Link to="/supply/samples/$id" params={{ id: route.id }} className={linkClass}>
            {conversation.entity_link.label}
          </Link>
        )
      case 'supply_purchase_order':
        return (
          <Link to="/supply/po-inbox" className={linkClass}>
            {conversation.entity_link.label}
          </Link>
        )
      default:
        return null
    }
  })()

  const panelClass =
    variant === 'expanded'
      ? 'rounded-[var(--radius-control)] border border-white/10 bg-white/[0.04] p-3'
      : 'rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-3'
  const labelClass =
    variant === 'expanded'
      ? 'text-[11px] font-semibold uppercase tracking-wide text-slate-400'
      : 'text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]'
  const entityTextClass =
    variant === 'expanded'
      ? 'mt-1 text-sm font-semibold text-white'
      : 'mt-1 text-sm font-semibold text-[var(--color-text)]'

  return (
    <aside
      className={panelClass}
      data-variant={variant === 'expanded' ? 'expanded' : undefined}
    >
      <p className={labelClass}>{linkLabel}</p>
      {entityLink ?? (
        <>
          <p className={entityTextClass}>
            {conversation.entity_link.label}
          </p>
          {surface !== 'admin' && !isSupportConversation && (
            <p className="mt-1 text-xs text-amber-800" role="status">
              {t.states.projectionUnavailable}
            </p>
          )}
        </>
      )}
    </aside>
  )
}
